import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/**
 * @typedef {{id:string|number, alt:string, aspect_ratio:number, src:string, src_small:string}} MediaJson
 * @typedef {{id:string|number, featured_media_id:string|number|null, option1:string|null, option2:string|null, option3:string|null}} VariantJson
 * @typedef {{product?: {media?: MediaJson[], variants?: VariantJson[]}}} ProductMediaJsonResponse
 */

function buildColorSynonyms(colorKey) {
  switch ((colorKey || '').trim().toLowerCase()) {
    case 'blue':
      return ['blue', 'navy', 'cyan', 'teal', 'azure'];
    case 'black':
      return ['black', 'charcoal'];
    case 'pink':
      return ['pink', 'magenta', 'rose'];
    case 'white':
      return ['white', 'ivory'];
    case 'green':
      return ['green', 'olive', 'sage', 'mint'];
    case 'red':
      return ['red', 'maroon', 'burgundy'];
    case 'yellow':
      return ['yellow', 'gold', 'mustard'];
    case 'purple':
      return ['purple', 'violet', 'lilac'];
    case 'orange':
      return ['orange', 'coral'];
    case 'brown':
      return ['brown', 'tan', 'khaki', 'camel'];
    default:
      return [];
  }
}

const KNOWN_COLOR_TOKENS = [
  'blue',
  'navy',
  'cyan',
  'teal',
  'azure',
  'black',
  'charcoal',
  'pink',
  'magenta',
  'rose',
  'white',
  'ivory',
  'green',
  'olive',
  'sage',
  'mint',
  'red',
  'maroon',
  'burgundy',
  'yellow',
  'gold',
  'mustard',
  'purple',
  'violet',
  'lilac',
  'orange',
  'coral',
  'brown',
  'tan',
  'khaki',
  'camel',
];

/**
 * Fetch product media JSON (theme view).
 * @param {string} productUrlOrHandle
 * @returns {Promise<MediaJson[]>}
 */
async function fetchProductMediaJson(productUrlOrHandle) {
  const handle = (() => {
    if (!productUrlOrHandle) return null;
    if (!productUrlOrHandle.includes('/')) return productUrlOrHandle;
    try {
      const u = new URL(productUrlOrHandle, window.location.origin);
      const m = u.pathname.match(/\/products\/([^/?#]+)/);
      return m?.[1] ?? null;
    } catch {
      return null;
    }
  })();

  if (!handle) return [];

  const url = new URL(`/products/${handle}`, window.location.origin);
  url.searchParams.set('view', 'media-json');
  url.searchParams.set('_cb', String(Date.now()));

  const requestUrl = url.toString();
  // IMPORTANT:
  // Some storefronts return the built-in product JSON when `Accept: application/json` is set,
  // ignoring `view=...`. We want the Liquid-rendered view template, so avoid forcing JSON here.
  const res = await fetch(requestUrl);

  if (!res.ok) {
    return [];
  }

  const text = await res.text();
  try {
    /** @type {ProductMediaJsonResponse} */
    const json = JSON.parse(text);
    if (json?.product && Array.isArray(json.product.media)) return json;

    // If we accidentally received the built-in product JSON, retry forcing HTML.
    const retry = await fetch(requestUrl, { headers: { Accept: 'text/html' } });
    const retryText = await retry.text();
    try {
      /** @type {ProductMediaJsonResponse} */
      const retryJson = JSON.parse(retryText);
      return retryJson?.product && Array.isArray(retryJson.product.media) ? retryJson : { product: { media: [], variants: [] } };
    } catch {
      return { product: { media: [], variants: [] } };
    }
  } catch (e) {
    return { product: { media: [], variants: [] } };
  }
}

/**
 * Read selected color from the modal's variant picker.
 * @param {HTMLElement} modalContent
 * @returns {string}
 */
function getSelectedColorFromModal(modalContent) {
  const picker = modalContent.querySelector('variant-picker');
  if (!picker) return '';
  const checked = picker.querySelectorAll('input[type="radio"]:checked');
  for (const input of checked) {
    if (!(input instanceof HTMLInputElement)) continue;
    const fieldset = input.closest('fieldset');
    const legend = fieldset?.querySelector('legend');
    const name = (legend?.textContent ?? '').trim().toLowerCase();
    if (name.includes('color') || name.includes('colour') || name.includes('颜色')) {
      return (input.value ?? '').trim();
    }
  }
  return '';
}

/**
 * Read selected variant id from the modal variant picker.
 * @param {HTMLElement} modalContent
 * @returns {string}
 */
function getSelectedVariantIdFromModal(modalContent) {
  const input = modalContent.querySelector('input[type="radio"][data-variant-id]:checked');
  if (input instanceof HTMLInputElement && input.dataset.variantId) return input.dataset.variantId;
  return '';
}

/**
 * Filter media by selected color using alt text (case-insensitive).
 * Rule:
 * - Include image if alt mentions selected color (or its synonyms)
 * - Also include "generic" images whose alt doesn't mention ANY known color token
 * - Exclude images whose alt mentions another color token
 * Fallback to all media if nothing matches.
 * @param {MediaJson[]} media
 * @param {string} selectedColor
 * @returns {MediaJson[]}
 */
function filterMediaByAlt(media, selectedColor) {
  const key = (selectedColor || '').trim().toLowerCase();
  if (!key) return media;

  const allowed = new Set([key, ...buildColorSynonyms(key)]);

  const matched = media.filter((m) => {
    const alt = (m.alt || '').toLowerCase();
    if (!alt) return true; // treat empty as generic

    let hasAllowed = false;
    let hasOther = false;

    for (const token of KNOWN_COLOR_TOKENS) {
      if (!alt.includes(token)) continue;
      if (allowed.has(token)) hasAllowed = true;
      else hasOther = true;
    }

    if (hasAllowed) return true;
    if (!hasOther) return true; // generic
    return false;
  });

  // Ensure we don't collapse to a single slide if the product has more images.
  const imageCount = matched.length;
  return imageCount ? matched : media;
}

/**
 * Reorder media so the selected variant featured media comes first.
 * @param {MediaJson[]} media
 * @param {VariantJson[]} variants
 * @param {string} selectedVariantId
 * @returns {MediaJson[]}
 */
function reorderMediaByVariantFeatured(media, variants, selectedVariantId) {
  if (!selectedVariantId || !variants?.length || !media?.length) return media;
  const v = variants.find((vv) => String(vv.id) === String(selectedVariantId));
  const featuredId = v?.featured_media_id;
  if (!featuredId) return media;
  const idx = media.findIndex((m) => String(m.id) === String(featuredId));
  if (idx <= 0) return media;
  return [media[idx], ...media.slice(0, idx), ...media.slice(idx + 1)];
}

/**
 * Build a slideshow-component DOM tree compatible with the theme slideshow element.
 * @param {MediaJson[]} media
 * @returns {HTMLElement}
 */
function buildSlideshow(media) {
  const slideshow = document.createElement('slideshow-component');
  slideshow.setAttribute('initial-slide', '0');
  slideshow.setAttribute('in-viewport', '');
  slideshow.classList.add('quick-add-media-slideshow');

  const container = document.createElement('slideshow-container');
  container.setAttribute('ref', 'slideshowContainer');

  if (media.length > 1) {
    const arrows = document.createElement('slideshow-arrows');
    arrows.setAttribute('position', 'center');

    const mkBtn = (action) => {
      const btn = document.createElement('button');
      btn.className =
        `slideshow-control slideshow-control--${action} slideshow-control--style-chevron ` +
        `button button-unstyled button-unstyled--transparent` +
        '';
      btn.setAttribute('aria-label', action);
      btn.setAttribute('type', 'button');
      return btn;
    };

    // Use existing svg if present in DOM; fallback to simple text.
    const prev = mkBtn('previous');
    const next = mkBtn('next');
    // Chevron SVG (lightweight, no dependency on inline_asset_content).
    const chevronSvg = `<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path d="M7.2 3.7a1 1 0 0 1 1.4 0l6 6a1 1 0 0 1 0 1.4l-6 6a1 1 0 1 1-1.4-1.4L12.5 10 7.2 4.7a1 1 0 0 1 0-1.4z" fill="currentColor"/>
    </svg>`;
    prev.innerHTML = chevronSvg;
    next.innerHTML = chevronSvg;
    prev.classList.add('quick-add-arrow', 'quick-add-arrow--prev');
    next.classList.add('quick-add-arrow', 'quick-add-arrow--next');

    arrows.appendChild(prev);
    arrows.appendChild(next);
    container.appendChild(arrows);

    // Bind clicks without relying on on:* parsing (we inject after initial HTML parse).
    const bindArrow = (btn, dir) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const any = /** @type {any} */ (slideshow);
        if (dir === 'next' && typeof any.next === 'function') return any.next(e, { animate: true });
        if (dir === 'previous' && typeof any.previous === 'function') return any.previous(e, { animate: true });

        // Fallback: bump initial-slide attribute (slideshow observes it and selects).
        const current = parseInt(slideshow.getAttribute('initial-slide') || '0', 10) || 0;
        const nextIndex = dir === 'next' ? Math.min(current + 1, media.length - 1) : Math.max(current - 1, 0);
        slideshow.setAttribute('initial-slide', String(nextIndex));
      });
    };
    bindArrow(prev, 'previous');
    bindArrow(next, 'next');
  }

  const slidesEl = document.createElement('slideshow-slides');
  slidesEl.setAttribute('tabindex', '-1');
  slidesEl.setAttribute('ref', 'scroller');

  media.forEach((m, i) => {
    const slide = document.createElement('slideshow-slide');
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
    slide.setAttribute('slide-id', `media-${m.id}`);
    slide.setAttribute('style', `--slideshow-timeline: --slide-${i}; --product-media-fit: cover;`);
    slide.classList.add('product-media-container', 'product-media-container--image');

    const mediaWrap = document.createElement('div');
    mediaWrap.className = 'product-media';
    mediaWrap.setAttribute('style', `--ratio: ${m.aspect_ratio || 1}`);
    mediaWrap.dataset.mediaId = String(m.id);

    const img = document.createElement('img');
    img.className = 'product-media__image';
    img.alt = m.alt || '';
    img.loading = i === 0 ? 'eager' : 'lazy';
    img.decoding = 'async';
    img.src = m.src;
    img.setAttribute('fetchpriority', i === 0 ? 'high' : 'auto');
    mediaWrap.appendChild(img);

    slide.appendChild(mediaWrap);
    slidesEl.appendChild(slide);
  });

  container.appendChild(slidesEl);
  slideshow.appendChild(container);
  return slideshow;
}

/**
 * Build a thumbnail bar for the quick-add slideshow.
 * @param {MediaJson[]} media
 * @param {HTMLElement} slideshow
 * @returns {HTMLElement}
 */
function buildThumbBar(media, slideshow) {
  const bar = document.createElement('div');
  bar.className = 'quick-add-modal-thumbnails';
  bar.setAttribute('role', 'tablist');
  bar.setAttribute('aria-label', 'Media thumbnails');

  const scrollThumbIntoView = (index) => {
    const btn = bar.querySelector(`button[data-index="${index}"]`);
    if (!(btn instanceof HTMLElement)) return;
    btn.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  };

  const selectSlide = (index, id) => {
    const any = /** @type {any} */ (slideshow);
    if (typeof any.select === 'function' && id) {
      any.select({ id }, undefined, { animate: false });
    } else {
      slideshow.setAttribute('initial-slide', String(index));
    }

    for (const btn of bar.querySelectorAll('button[aria-selected="true"]')) {
      btn.setAttribute('aria-selected', 'false');
    }
    const current = bar.querySelector(`button[data-index="${index}"]`);
    if (current) current.setAttribute('aria-selected', 'true');
    scrollThumbIntoView(index);
  };

  media.forEach((m, i) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'quick-add-modal-thumbnail';
    btn.dataset.index = String(i);
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-label', `Slide ${i + 1}`);
    btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');

    const img = document.createElement('img');
    img.alt = m.alt || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = m.src_small || m.src;
    btn.appendChild(img);

    const slideId = `media-${m.id}`;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      selectSlide(i, slideId);
    });

    bar.appendChild(btn);
  });

  // Sync selection when user swipes/scrolls the slideshow.
  slideshow.addEventListener('slideshow:select', (e) => {
    const detail = /** @type {any} */ (e).detail;
    const idx = typeof detail?.index === 'number' ? detail.index : null;
    if (idx == null) return;
    for (const btn of bar.querySelectorAll('button[aria-selected="true"]')) {
      btn.setAttribute('aria-selected', 'false');
    }
    const current = bar.querySelector(`button[data-index="${idx}"]`);
    if (current) current.setAttribute('aria-selected', 'true');
    scrollThumbIntoView(idx);
  });

  return bar;
}

/**
 * Replace modal media-gallery with JSON-driven slideshow.
 * @param {HTMLElement} modalContent
 */
async function hydrateQuickAddMediaFromJson(modalContent) {
  const mediaArea =
    modalContent.querySelector('.product-information__media') ||
    modalContent.querySelector('[data-testid="product-media"]') ||
    modalContent;

  const productLink =
    modalContent.querySelector('.product-header a[href]') ||
    modalContent.querySelector('a[href*="/products/"]');

  const href = productLink instanceof HTMLAnchorElement ? productLink.href : '';
  const resp = await fetchProductMediaJson(href);
  const allMedia = Array.isArray(resp?.product?.media) ? resp.product.media : [];
  const variants = Array.isArray(resp?.product?.variants) ? resp.product.variants : [];

  const selectedColor = getSelectedColorFromModal(modalContent);
  const selectedVariantId = getSelectedVariantIdFromModal(modalContent);

  let filtered = filterMediaByAlt(allMedia, selectedColor);
  filtered = reorderMediaByVariantFeatured(filtered, variants, selectedVariantId);

  // Keep existing media-gallery container (it carries CSS variables, radius, sizing).
  // Only replace its slideshow content with the JSON-driven slideshow.
  const existingGallery = mediaArea.querySelector('media-gallery');
  const gallery = existingGallery instanceof HTMLElement ? existingGallery : null;

  // Ensure the container behaves like carousel in the modal.
  if (gallery) {
    gallery.classList.remove('media-gallery--grid');
    gallery.classList.add('media-gallery--carousel');
    gallery.classList.add('quick-add-media-gallery--json');
    gallery.dataset.presentation = 'carousel';
    gallery.setAttribute('data-presentation', 'carousel');
  }
  const existingThumbs = mediaArea.querySelector('.quick-add-modal-thumbnails');
  if (existingThumbs) existingThumbs.remove();

  const scope = gallery ?? mediaArea;
  const existingSlideshow = scope.querySelector('slideshow-component');
  if (existingSlideshow) existingSlideshow.remove();

  const slideshow = buildSlideshow(filtered);
  if (gallery) {
    // Remove any legacy grid markup inside gallery so it doesn't interfere.
    const grid = gallery.querySelector('.media-gallery__grid');
    if (grid instanceof HTMLElement) grid.remove();
    gallery.prepend(slideshow);
  } else {
    mediaArea.prepend(slideshow);
  }

  // Thumbnails (only when multiple images). Place inside media area so it stays visible.
  if (filtered.length > 1) {
    const bar = buildThumbBar(filtered, slideshow);
    // Place thumbnails directly under slideshow.
    slideshow.insertAdjacentElement('afterend', bar);
  }
}

/**
 * Apply quick-add modal specific media behavior:
 * - Disable zoom UI/handlers
 * - Remove built-in slideshow controls (which quick-add CSS may hide and leave blank space)
 * - Inject a modal-only thumbnail strip to control the slideshow reliably
 *
 * @param {Element | Document} root
 */
function applyQuickAddModalMediaFixes(root) {
  const modalContent = root.getElementById
    ? root.getElementById('quick-add-modal-content')
    : root.querySelector?.('#quick-add-modal-content');
  if (!(modalContent instanceof HTMLElement)) return;

  const mediaArea =
    modalContent.querySelector('.product-information__media') ||
    modalContent.querySelector('[data-testid="product-media"]') ||
    modalContent;

  const mediaGallery = mediaArea.querySelector('media-gallery');
  if (!(mediaGallery instanceof HTMLElement)) return;

  // Disable zoom UI/handlers inside the modal.
  for (const zoomButton of mediaGallery.querySelectorAll('.product-media-container__zoom-button')) {
    zoomButton.remove();
  }
  for (const zoomDialog of mediaGallery.querySelectorAll('zoom-dialog')) {
    zoomDialog.remove();
  }
  for (const el of mediaGallery.querySelectorAll('[on\\:click]')) {
    if (!(el instanceof HTMLElement)) continue;
    const onClick = el.getAttribute('on:click') ?? '';
    if (onClick.includes('/zoom/') || onClick.includes('#zoom-dialog-')) {
      el.removeAttribute('on:click');
    }
  }

  // Force enable scrolling for any slideshows in the modal (dialog + IO can be flaky).
  for (const el of mediaGallery.querySelectorAll('slideshow-component')) {
    if (!(el instanceof HTMLElement)) continue;
    el.setAttribute('in-viewport', '');
    el.removeAttribute('disabled');
  }

  // Remove any built-in controls in the modal (quick-add CSS can hide them but still reserve layout space).
  for (const controls of mediaGallery.querySelectorAll('slideshow-controls')) {
    controls.remove();
  }

  const slideshowEl = mediaGallery.querySelector('slideshow-component');
  if (!(slideshowEl instanceof HTMLElement)) return;

  const slides = Array.from(slideshowEl.querySelectorAll('slideshow-slide'));

  // Remove any existing injected thumb bar.
  const existing = mediaArea.querySelector('.quick-add-modal-thumbnails');
  if (existing) existing.remove();

  if (slides.length <= 1) return;

  const thumbBar = document.createElement('div');
  thumbBar.className = 'quick-add-modal-thumbnails';
  thumbBar.setAttribute('role', 'tablist');
  thumbBar.setAttribute('aria-label', 'Media thumbnails');

  slides.forEach((slide, index) => {
    const img = slide.querySelector('img');
    if (!(img instanceof HTMLImageElement)) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'quick-add-modal-thumbnail';
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-label', `Slide ${index + 1}`);
    btn.setAttribute('aria-selected', index === 0 ? 'true' : 'false');

    const thumbImg = document.createElement('img');
    thumbImg.alt = img.alt || '';
    thumbImg.loading = 'lazy';
    thumbImg.decoding = 'async';
    thumbImg.src = img.currentSrc || img.src;
    btn.appendChild(thumbImg);

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      // Use attribute-driven selection so it works even before the custom element is upgraded.
      slideshowEl.setAttribute('initial-slide', String(index));
      for (const other of thumbBar.querySelectorAll('button[aria-selected="true"]')) {
        other.setAttribute('aria-selected', 'false');
      }
      btn.setAttribute('aria-selected', 'true');
    });

    thumbBar.appendChild(btn);
  });

  // Place thumbnails right under the media gallery.
  mediaGallery.insertAdjacentElement('afterend', thumbBar);
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  /**
   * Find the product link in a resilient way. Some pages may render a plain element
   * that matches `product-card` but is not upgraded to the custom element yet.
   * @returns {HTMLAnchorElement | null}
   */
  #getProductLink() {
    const productCard = /** @type {Element | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {Element | null} */ (this.closest('product-hotspot-component'));

    const cardLink =
      productCard &&
      // @ts-expect-error - may be a custom element with helper methods
      typeof productCard.getProductCardLink === 'function'
        ? // @ts-expect-error - method exists at runtime on the custom element
          productCard.getProductCardLink()
        : null;

    const hotspotLink =
      hotspotProduct &&
      // @ts-expect-error - may be a custom element with helper methods
      typeof hotspotProduct.getHotspotProductLink === 'function'
        ? // @ts-expect-error - method exists at runtime on the custom element
          hotspotProduct.getHotspotProductLink()
        : null;

    const preferred = (cardLink || hotspotLink) instanceof HTMLAnchorElement ? (cardLink || hotspotLink) : null;
    if (preferred?.href) return preferred;

    // Fallbacks for themes/sections that don't use the custom elements.
    const closestProductAnchor = /** @type {HTMLAnchorElement | null} */ (
      this.closest('[data-product-url]')?.querySelector?.('a[href*="/products/"]') ||
        this.closest('article, li, div')?.querySelector?.('a[href*="/products/"]') ||
        null
    );
    if (closestProductAnchor?.href) return closestProductAnchor;

    const dataUrlEl = /** @type {HTMLElement | null} */ (this.closest('[data-product-url]'));
    const dataUrl = dataUrlEl?.getAttribute('data-product-url') || '';
    if (dataUrl) {
      const a = document.createElement('a');
      a.href = dataUrl;
      return a;
    }

    return null;
  }

  get productPageUrl() {
    const productLink = this.#getProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {Element | null} */ (this.closest('product-card'));
    // @ts-expect-error - may be a custom element with helper methods
    return productCard && typeof productCard.getSelectedVariantId === 'function'
      ? // @ts-expect-error - method exists at runtime on the custom element
        productCard.getSelectedVariantId()
      : null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const url = new URL(productPageUrl, window.location.origin);
      // Avoid stale HTML when iterating on theme Liquid (Shopify/CDN/browser caching).
      url.searchParams.set('_cb', String(Date.now()));

      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#usePdpMediaGallery(modalContent);
    // B方案：只在弹窗打开时拉 media-json 并重建轮播
    hydrateQuickAddMediaFromJson(modalContent);
    applyQuickAddModalMediaFixes(document);
    this.#syncVariantSelection(modalContent);

    // Re-apply after any variant-driven DOM updates inside the modal.
    window.setTimeout(() => {
      this.#usePdpMediaGallery(modalContent);
      hydrateQuickAddMediaFromJson(modalContent);
      applyQuickAddModalMediaFixes(document);
    }, 0);
    window.setTimeout(() => {
      this.#usePdpMediaGallery(modalContent);
      hydrateQuickAddMediaFromJson(modalContent);
      applyQuickAddModalMediaFixes(document);
    }, 200);
  }

  /**
   * Make quick-add modal use the same media gallery mode as PDP (carousel).
   * We only mutate inside the modal so other pages stay untouched.
   * @param {Element} modalContent
   */
  #usePdpMediaGallery(modalContent) {
    const mediaGallery = modalContent.querySelector('media-gallery');
    if (!(mediaGallery instanceof HTMLElement)) return;

    // If the content was rendered as grid, switch it to carousel so PDP styles/behavior apply.
    if (mediaGallery.classList.contains('media-gallery--grid')) {
      mediaGallery.classList.remove('media-gallery--grid');
      mediaGallery.classList.add('media-gallery--carousel');
      mediaGallery.dataset.presentation = 'carousel';
      mediaGallery.setAttribute('data-presentation', 'carousel');
    }

    // Ensure grid markup doesn't show alongside the carousel inside the modal.
    const grid = mediaGallery.querySelector('.media-gallery__grid');
    if (grid instanceof HTMLElement) grid.style.display = 'none';

    const slideshow = mediaGallery.querySelector('slideshow-component');
    if (slideshow instanceof HTMLElement) slideshow.style.display = '';
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {number | null} */
  #rehydrateTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(
      ThemeEvents.variantUpdate,
      () => {
        // media-gallery is replaced on variant update; re-apply modal-only media fixes.
        if (this.#rehydrateTimer) window.clearTimeout(this.#rehydrateTimer);
        this.#rehydrateTimer = window.setTimeout(() => {
          const modalContent = document.getElementById('quick-add-modal-content');
          if (modalContent instanceof HTMLElement) hydrateQuickAddMediaFromJson(modalContent);
          applyQuickAddModalMediaFixes(document);
        }, 0);
      },
      { signal: this.#abortController.signal }
    );

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
